import React, {
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import cx from 'classnames';

import useInView from 'Utilities/useInView';

import { Content } from 'LegacySBCRA/Content/Content';

import styles from './Carousel.scss';

type CarouselProps = {
  children?: React.ReactNode;
  slideInterval?: number[];
  vertical?: boolean;
  wrapperClassName?: string;
};

export const Carousel: FC<CarouselProps> = ({
  children = null,
  slideInterval = [1000, 2000],
  vertical = false,
  wrapperClassName = '',
}) => {
  const [index, setIndex] = useState(0);
  const [mounted, setMounted] = useState(false);

  const $container = useRef<HTMLDivElement>(null);
  const timer = useRef<NodeJS.Timeout | undefined>(undefined);

  const visible = useInView($container, {
    rootMargin: '100px',
  });

  const animationInterval = slideInterval[(vertical) ? 0 : 1];

  const renderChildren = () => React.Children.toArray(children)
    .map((child, i) => React.cloneElement(child as React.ReactElement, {
      // @ts-expect-error TODO: TypeScript doesn't know child's props structure
      className: cx((child as React.ReactElement).props.className, {
        [styles.index]: i === index + 2,
        [styles.preIndex]: i === index + 1,
      }),
    }));

  const updateIndex = useCallback(() => {
    setIndex((oldIndex) => {
      const offset = (vertical) ? 14 : 8;

      if ((oldIndex + offset) === React.Children.toArray(children).length) {
        return 0;
      }

      return (oldIndex + 1);
    });
  }, [children, setIndex, vertical]);

  const triggerNewTransition = useCallback((e?: React.TransitionEvent<HTMLDivElement>) => {
    e?.stopPropagation?.();

    if (e && e?.nativeEvent?.propertyName !== 'transform') {
      return;
    }

    clearTimeout(timer.current);
    timer.current = setTimeout(updateIndex, 1000);
  }, [updateIndex]);

  useEffect(() => {
    if (!mounted && visible) {
      triggerNewTransition();
      setMounted(true);
    }
  }, [mounted, triggerNewTransition, visible]);

  const childCount = React.Children.count(children);

  const calculatedTransform = (vertical)
    ? `translate3d(0, calc(((100% / ${childCount}) * ${index}) * -1), 0)`
    : `translate3d(calc(((100% / ${childCount}) * ${index + 2}) * -1), 0, 0)`;

  const finalStyle = { transform: calculatedTransform, transition: `transform ${animationInterval}ms ease-out` };

  return (
    <div className={cx(styles.overflowContainer, wrapperClassName)} style={{ '--recent-sales-carousel-items': childCount } as React.CSSProperties}>
      <Content className={styles.content}>
        <div className={styles.wrapper}>
          <div
            onTransitionEnd={triggerNewTransition}
            style={(mounted && vertical) ? finalStyle : {}}
          >
            <div
              ref={$container}
              className={styles.container}
              onTransitionEnd={triggerNewTransition}
              style={(mounted && !vertical) ? finalStyle : {}}
            >
              { renderChildren() }
            </div>
          </div>
        </div>
      </Content>
    </div>
  );
};
